/**
 * TasksView: the agency Tasks tab (DD-049, TD-094). The /clients page
 * shell: status tabs and filters in the page header, then one fixed card
 * whose body scrolls, listing every client task grouped under a
 * ClientBadge header. Each group folds from its header, and the folded
 * set survives a reload. Filters are component state, so a reload starts
 * on everyone's open tasks. A row opens the task drawer (DD-017), which
 * shows the task's client and asks for one on create.
 */

import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, PageLayout, PageSection } from "@/components/PageLayout";
import { ChevronDownIcon, SpinnerIcon } from "@/components/icons";
import { OptionDropdown } from "@/components/ui/OptionDropdown";
import { TabGroup, TabItem } from "@/components/ui/TabGroup";
import { ENTITY_CONFIG, EntityCreateButton } from "@/core/entityConfig";
import { HttpClientProvider } from "@/core/http/HttpClientContext";
import type { HttpClient } from "@/core/http/types";
import { useMediaQuery } from "@/core/http/useMediaQuery";
import { useTrailNavOptions } from "@/core/navigation/trail";
import { ClientBadge } from "@/domains/Client/components/ClientBadge";
import { useAgencyPeopleQuery } from "@/domains/Client/useAgencyPeople";
import { useClients } from "@/domains/Client/useClients";
import { apiClient } from "@/services/apiClient";
import { cn } from "@/utils/cn";
import {
  AGENCY_TASK_CAPABILITIES,
  ALL_PEOPLE,
  countLabel,
  personFilterOptions,
  summaryLabel,
} from "./agencyTasks";
import { TaskRow } from "./components/TaskRow";
import { TaskDrawer } from "./drawer/TaskDrawer";
import { useTaskDrawer } from "./drawer/useTaskDrawer";
import { useCollapsedClients } from "./hooks/useCollapsedClients";
import { useAgencyTasksQuery, useToggleTask } from "./hooks/useTasks";
import type { AgencyTaskGroup, TaskStatus } from "./types";

const STATUS_TABS: TabItem<TaskStatus>[] = [
  { id: "open", label: "Aperti" },
  { id: "done", label: "Fatti" },
];

function TasksPage() {
  const trailNavOptions = useTrailNavOptions(ENTITY_CONFIG.task.plural);
  const [status, setStatus] = useState<TaskStatus>("open");
  const [assignee, setAssignee] = useState<string | null>(null);
  const { isCollapsed, toggle } = useCollapsedClients();
  // On a phone the person filter is the first item of the actions row, at
  // the screen's left edge (DD-058), so its menu opens rightward; from md up
  // it sits in the right-hand cluster and opens leftward, as before.
  const wide = useMediaQuery("(min-width: 48rem)");

  // isPlaceholderData: a filter switch shows the previous list, dimmed,
  // until the new one lands. Only the first load shows the spinner (DD-001).
  const { data, isLoading, isError, isPlaceholderData } = useAgencyTasksQuery({ status, assignee });
  // The status of the list on screen. While a switch is in flight the
  // dimmed rows still belong to the previous status, so labels use this.
  const settledStatus = useRef<TaskStatus>(status);
  if (data && !isPlaceholderData) settledStatus.current = status;
  const shownStatus = settledStatus.current;
  const { data: people } = useAgencyPeopleQuery();
  const { clients } = useClients();
  const toggleTask = useToggleTask();
  const drawer = useTaskDrawer();

  const personOptions = useMemo(() => personFilterOptions(people ?? []), [people]);
  const TaskEntityIcon = ENTITY_CONFIG.task.icon;
  const groups = data?.groups ?? [];

  const renderGroup = (group: AgencyTaskGroup) => {
    const folded = isCollapsed(group.client.id);
    return (
      <section key={group.client.id} className="py-2">
        {/* The whole header row folds the group; the badge stays a link to
            the client, painted above the row button. */}
        <div className="relative -mx-2 flex items-center gap-2 px-2 py-1.5">
          <button
            type="button"
            onClick={() => toggle(group.client.id)}
            aria-expanded={!folded}
            aria-label={folded ? `Espandi ${group.client.name}` : `Comprimi ${group.client.name}`}
            className="absolute inset-0 rounded-md hover:bg-gray-50"
          />
          <ChevronDownIcon
            className={cn(
              "pointer-events-none relative h-4 w-4 shrink-0 text-gray-400 transition-transform",
              folded && "-rotate-90"
            )}
          />
          <Link
            to={`/app/${group.client.id}`}
            className="relative flex min-w-0 rounded-md hover:opacity-80"
            title={group.client.name}
          >
            <ClientBadge name={group.client.name} size="sm" className="min-w-0" />
          </Link>
          <span className="pointer-events-none relative ml-auto shrink-0 text-xs text-gray-400">
            {countLabel(group.tasks.length, shownStatus)}
          </span>
        </div>
        {!folded && (
          <div className="divide-y divide-gray-50">
            {group.tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                capabilities={AGENCY_TASK_CAPABILITIES}
                onToggle={toggleTask}
                onOpen={drawer.open}
              />
            ))}
          </div>
        )}
      </section>
    );
  };

  const emptyState = (
    <div className="text-center py-12">
      <TaskEntityIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
      <p className="text-sm font-medium text-gray-900">
        {shownStatus === "open" ? "Nessun task aperto" : "Nessun task fatto"}
      </p>
      <p className="text-sm text-gray-500 mt-1">
        {assignee
          ? "Cambia persona per vedere altri task."
          : shownStatus === "open"
            ? "I task aperti di ogni cliente appaiono qui."
            : "I task completati di recente appaiono qui."}
      </p>
    </div>
  );

  return (
    <PageLayout>
      <PageHeader>
        <PageHeader.Row>
          <TabGroup
            items={STATUS_TABS}
            activeId={status}
            onTabChange={setStatus}
            config={{
              variant: "navigation",
              size: "sm",
              layout: "horizontal",
              hasBackground: true,
            }}
          />
          <PageHeader.Actions>
            {data && !isPlaceholderData && (
              <span className="hidden sm:inline text-xs text-gray-400">
                {summaryLabel(data.count, status)}
              </span>
            )}
            <OptionDropdown
              value={assignee ?? ALL_PEOPLE}
              options={personOptions}
              onChange={(value) => setAssignee(value === ALL_PEOPLE ? null : value)}
              align={wide ? "right" : "left"}
              width="w-56"
            />
            <EntityCreateButton
              entity="task"
              onClick={() => drawer.create({ clientId: null, links: [] })}
            />
          </PageHeader.Actions>
        </PageHeader.Row>
      </PageHeader>

      <PageSection.Card>
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center" data-testid="tasks-loading">
            <SpinnerIcon className="w-5 h-5 text-gray-400" />
          </div>
        ) : isError ? (
          <p className="p-4 text-sm text-red-600">Impossibile caricare i task.</p>
        ) : (
          <div
            className={cn(
              "px-4 py-2 transition-opacity",
              isPlaceholderData && "opacity-60 pointer-events-none"
            )}
            aria-busy={isPlaceholderData}
          >
            {groups.length === 0 ? (
              emptyState
            ) : (
              <div className="divide-y divide-gray-100">{groups.map(renderGroup)}</div>
            )}
          </div>
        )}
      </PageSection.Card>

      <TaskDrawer
        drawer={drawer}
        capabilities={AGENCY_TASK_CAPABILITIES}
        clients={clients}
        navOptions={trailNavOptions}
      />
    </PageLayout>
  );
}

/** AdminLayout mounts no HttpClientProvider (MainApp mounts one per client
 * workspace), so the tab mounts the admin client for the task hooks, the
 * same way MainApp does. */
export function TasksView() {
  return (
    <HttpClientProvider client={apiClient as unknown as HttpClient}>
      <TasksPage />
    </HttpClientProvider>
  );
}
